import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import { Box, Typography, Card, CardContent, Grid, Button } from '@mui/material';
import Article from '@mui/icons-material/Article';
import PendingActions from '@mui/icons-material/PendingActions';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';
import { fetchPosts } from '../../store/slices/postsSlice';
import { fetchNotifications } from '../../store/slices/notificationsSlice';

export default function DashboardHome() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((s) => s.auth);
  const { list: posts, total: postsTotal, stats: postsStats } = useSelector((s) => s.posts);
  const { unreadCount } = useSelector((s) => s.notifications);
  const role = user?.role || '';

  useEffect(() => {
    dispatch(fetchPosts({ limit: 20, page: 1 }));
    dispatch(fetchNotifications());
  }, [dispatch]);

  const myPostCount = postsTotal ?? (role === 'admin' || role === 'moderator' ? posts.length : posts.filter((p) => p.author?._id === user?._id || p.author === user?._id).length);
  const pendingCount = postsStats?.inConsultation ?? posts.filter((p) => p.status === 'inConsultation').length;
  const canSeePending = role === 'admin' || role === 'moderator';

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" color="text.primary" gutterBottom>
        Bienvenue, {user?.fullname || 'Utilisateur'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Rôle : {role}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" variant="body2">Mes articles</Typography>
                    <Typography variant="h4" fontWeight="bold" color="primary">{myPostCount}</Typography>
                  </Box>
                  <Article sx={{ fontSize: 40, color: 'primary.main', opacity: 0.8 }} />
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
        {canSeePending && (
          <Grid item xs={12} sm={6} md={4}>
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
              <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)', cursor: 'pointer' }} onClick={() => navigate('/dashboard/pending')}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography color="text.secondary" variant="body2">En consultation</Typography>
                      <Typography variant="h4" fontWeight="bold" color="warning.main">{pendingCount}</Typography>
                    </Box>
                    <PendingActions sx={{ fontSize: 40, color: 'warning.main', opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        )}
        <Grid item xs={12} sm={6} md={4}>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card sx={{ borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography color="text.secondary" variant="body2">Notifications</Typography>
                    <Typography variant="h4" fontWeight="bold" color="primary">{unreadCount}</Typography>
                  </Box>
                  <Button size="small" onClick={() => navigate('/dashboard/notifications')}>Voir</Button>
                </Box>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>
      <Box sx={{ mt: 3 }}>
        <Button variant="contained" startIcon={<AddCircleOutline />} onClick={() => navigate('/dashboard/posts/new')}>
          Créer un article
        </Button>
      </Box>
    </Box>
  );
}
